import { type PayloadAction, createSlice } from '@reduxjs/toolkit';
import type { TRootState } from 'slices/store';
import { createAppAsyncThunk } from 'slices/utils';
import {
  ESeatingMethod,
  ESeatingMode,
  type ISeatAllocationSettings,
  type TExamV3ReservationSettings,
} from 'types/examV3Settings.type';
import type { IDefaultReducerState } from 'types/state.interface';
import {
  finishedLoadingFailure,
  finishedLoadingSuccess,
  isLoadingRequest,
} from 'utils/sliceHelpers';
import api from '../services/api.service';

export type TExamV3SettingsState = IDefaultReducerState & {
  reservationSettings: TExamV3ReservationSettings;
  seatAllocationSettings: ISeatAllocationSettings;
};

export const initialState: TExamV3SettingsState = {
  loading: false,
  hasErrors: false,
  seatAllocationSettings: {
    seatingMode: {
      mode: ESeatingMode.StudentSpecific,
      allowOverride: false,
    },
    seatingMethod: {
      mode: ESeatingMethod.clumped,
      allowOverride: false,
    },
    layoutPattern: undefined,
    sorting: {
      allowOverride: false,
      sort: 'asc',
    },
  },
  reservationSettings: {
    examReservationMode: undefined,
    sizeReservationTemplate: undefined,
    capacityReservationTemplate: undefined,
    requiredInvigilatorNumber: undefined,
  },
};

// Slice
const slice = createSlice({
  name: 'examV3ReservationSettings',
  initialState,
  reducers: {
    fetchReservationSettingsRequest: (state) => {
      isLoadingRequest(state);
    },
    fetchReservationSettingsSuccess: (
      state,
      { payload }: PayloadAction<TExamV3ReservationSettings>
    ) => {
      state.reservationSettings = payload;
      finishedLoadingSuccess(state);
    },
    fetchReservationSettingsFailure: (state) => {
      finishedLoadingFailure(state);
    },
    saveReservationSettingsRequest: (state) => {
      isLoadingRequest(state);
    },
    saveReservationSettingsSuccess: (state) => {
      finishedLoadingSuccess(state);
    },
    saveReservationSettingsFailure: (state) => {
      finishedLoadingFailure(state);
    },
    selectReservationOption: (
      state,
      {
        payload,
      }: PayloadAction<Record<keyof TExamV3ReservationSettings, number>>
    ) => {
      state.reservationSettings = {
        ...state.reservationSettings,
        ...payload,
      };
    },
    fetchAllocationModeRequest: (state) => {
      isLoadingRequest(state);
    },
    fetchAllocationModeSuccess: (
      state,
      { payload }: PayloadAction<ISeatAllocationSettings>
    ) => {
      state.seatAllocationSettings = payload;
      finishedLoadingSuccess(state);
    },
    fetchAllocationModeFailure: (state) => {
      finishedLoadingFailure(state);
    },
    saveAllocationModeRequest: (state) => {
      isLoadingRequest(state);
    },
    saveAllocationModeSuccess: (state) => {
      finishedLoadingSuccess(state);
    },
    saveAllocationModeFailure: (state) => {
      finishedLoadingFailure(state);
    },
    updateSeatAllocateSetting: (
      state,
      { payload }: PayloadAction<Partial<ISeatAllocationSettings>>
    ) => {
      state.seatAllocationSettings = {
        ...state.seatAllocationSettings,
        ...payload,
      };
    },
  },
});

export default slice.reducer;

// Selectors
export const examV3ReservationSettingsSelector = (
  state: TRootState
): TExamV3ReservationSettings => state.examV3Settings.reservationSettings;

export const examV3SettingsLoadingSelector = (state: TRootState): boolean =>
  state.examV3Settings.loading;

export const examV3ReservationSettingsLoadingSelector = (state: TRootState) =>
  state.examV3Settings.loading;

// Selectors
export const seatAllocationSettingsSelector = (
  state: TRootState
): ISeatAllocationSettings => state.examV3Settings.seatAllocationSettings;

// Actions
export const {
  // reservation settings
  fetchReservationSettingsRequest,
  fetchReservationSettingsSuccess,
  fetchReservationSettingsFailure,
  saveReservationSettingsRequest,
  saveReservationSettingsSuccess,
  saveReservationSettingsFailure,
  selectReservationOption,

  // allocation mode
  fetchAllocationModeRequest,
  fetchAllocationModeSuccess,
  fetchAllocationModeFailure,
  saveAllocationModeRequest,
  saveAllocationModeSuccess,
  saveAllocationModeFailure,
  updateSeatAllocateSetting,
} = slice.actions;

export const fetchExamV3ReservationSettings = createAppAsyncThunk(
  'examV3Settings/fetchReservationSetting',
  async (_, { dispatch }) => {
    try {
      dispatch(fetchReservationSettingsRequest());
      const result = await api.get({
        endpoint: `/apps/TE_EXAM_V3/reservation-settings`,
      });
      dispatch(fetchReservationSettingsSuccess(result?.reservationSettings));
    } catch (e) {
      dispatch(fetchReservationSettingsFailure());
      console.error(e);
    }
  }
);

export const saveExamV3ReservationSettings = createAppAsyncThunk(
  'examV3Settings/saveSeatAllocationSettings',
  async (_, { dispatch, getState }) => {
    try {
      dispatch(saveReservationSettingsRequest());
      const reservationSettings = examV3ReservationSettingsSelector(getState());

      await api.patch({
        endpoint: '/apps/TE_EXAM_V3/reservation-settings',
        data: reservationSettings,
      });
      dispatch(saveReservationSettingsSuccess());
    } catch (error) {
      dispatch(saveReservationSettingsFailure());
      console.error(error);
    }
  }
);

export const fetchAllocationMode = createAppAsyncThunk(
  'examV3Settings/fetchSeatAllocationSettings',
  async (_, { dispatch }) => {
    try {
      dispatch(fetchAllocationModeRequest());
      const result = (await api.get({
        endpoint: `/apps/TE_EXAM_V3/seat-allocation-settings`,
      })) as { seatAllocationSettings: ISeatAllocationSettings };
      dispatch(fetchAllocationModeSuccess(result.seatAllocationSettings));
    } catch (e) {
      dispatch(fetchAllocationModeFailure());
      console.error(e);
    }
  }
);

export const saveAllocationMode = createAppAsyncThunk(
  'examV3Settings/saveSeatAllocationSettings',
  async (_, { dispatch, getState }) => {
    try {
      dispatch(saveAllocationModeRequest());
      const seatAllocationSettings = seatAllocationSettingsSelector(getState());

      await api.patch({
        endpoint: '/apps/TE_EXAM_V3/seat-allocation-settings',
        data: { seatAllocationSettings },
      });
      dispatch(saveAllocationModeSuccess());
    } catch (error) {
      dispatch(saveAllocationModeFailure());
      console.error(error);
    }
  }
);
